// DepartmentDatePicker.js
import * as React from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

/**
 * 部門期間日付選択コンポーネント
 *
 * @param {Object} props
 * @param {Date | null} props.selected - 選択された日付
 * @param {(date: Date | null) => void} props.onChange - 日付選択時に呼び出されるコールバック関数
 * @param {boolean} [props.disabled] - 入力を無効化するかどうか
 * @param {Date} [props.minDate] - 選択可能な最小日付
 * @param {Date} [props.maxDate] - 選択可能な最大日付
 * @param {string} [props.className] - 入力要素に適用するクラス名
 * @param {string} [props.placeholderText] - プレースホルダー
 * @param {string} [props.id] - Input要素のID
 * @param {Object} [props.style] - Inline style
 * @returns {JSX.Element}
 */
const DepartmentDatePicker = ({
  selected,
  onChange,
  disabled,
  minDate,
  maxDate,
  className,
  placeholderText,
  id,
  style
}) => {
  // カレンダーに表示中の月を保持するRef
  const monthInCalendarRef = React.useRef(selected ? selected.getMonth() : new Date().getMonth())

  // minDate/maxDateのデフォルト値を設定（制限なしの場合）
  const effectiveMinDate = minDate || new Date(1900, 0, 1)
  const effectiveMaxDate = maxDate || new Date(2100, 11, 31)

  return (
    <DatePicker
      id={id}
      locale="ja"
      selected={selected}
      onChange={onChange}
      dateFormat="yyyy/MM/dd"
      placeholderText={placeholderText}
      className={className}
      style={style}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      disabled={disabled}
      minDate={effectiveMinDate}
      maxDate={effectiveMaxDate}
      openToDate={minDate || undefined}
      renderDayContents={(day, date) => {
        const isCurrentMonth = date.getMonth() === monthInCalendarRef.current
        return <span className={isCurrentMonth ? 'text-black' : 'text-gray-400'}>{day}</span>
      }}
      // カスタムヘッダーのレンダリング（年月選択と前後月ボタン）
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled
      }) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        monthInCalendarRef.current = month // 現在のカレンダー月を更新

        // minDateからmaxDateまでの年リストを作成
        const years = []
        for (let y = effectiveMinDate.getFullYear(); y <= effectiveMaxDate.getFullYear(); y++) {
          years.push(y)
        }

        // 現在年に対して選択可能な月リストを作成
        let months = []
        if (year === effectiveMinDate.getFullYear() && year === effectiveMaxDate.getFullYear()) {
          for (let m = effectiveMinDate.getMonth(); m <= effectiveMaxDate.getMonth(); m++) months.push(m)
        } else if (year === effectiveMinDate.getFullYear()) {
          for (let m = effectiveMinDate.getMonth(); m < 12; m++) months.push(m)
        } else if (year === effectiveMaxDate.getFullYear()) {
          for (let m = 0; m <= effectiveMaxDate.getMonth(); m++) months.push(m)
        } else {
          months = [...Array(12).keys()] // 0〜11
        }

        // ヘッダーをレンダリング
        return (
          <div className="m-2 flex justify-center items-center gap-2">
            {/* 前月ボタン */}
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className="text-sm px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {'<<'}
            </button>

            {/* 年選択 */}
            <select
              value={year}
              onChange={(e) => changeYear(Number(e.target.value))}
              className="px-2 py-1 border font-bold rounded border-gray-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="text-h7">年</span>

            {/* 月選択 */}
            <select
              value={month}
              onChange={(e) => changeMonth(Number(e.target.value))}
              className="px-2 py-1 font-bold border rounded border-gray-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {m + 1}
                </option>
              ))}
            </select>
            <span className="text-h7">月</span>

            {/* 翌月ボタン */}
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className="text-sm px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              {'>>'}
            </button>
          </div>
        )
      }}
    />
  )
}

export { DepartmentDatePicker }
